import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Menu, Typography, Grid, Button } from 'antd';
import { CaretDownFilled, MenuOutlined } from '@ant-design/icons';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { openid } from '@/core';
import { useUserContextSelector } from '@/redux';
import { Avatar, LogoIcon, confirmation } from '@/components';
import { useSelectedNavMenuKeys } from './useSelectedNavMenuKeys';
import { BurgerMenu } from './BurgerMenu';
import classes from './Layout.module.scss';

const { useBreakpoint } = Grid;

function useNavMenu() {
  const { t } = useTranslation();
  const [name, avatar] = useUserContextSelector((x) => [x.name, x.avatar]);
  const hasRole = useUserContextSelector((x) => x.hasRole);

  const showSignOutConfirmationDialog = () => {
    confirmation.show({
      title: t('logoutConfirmation.title'),
      body: t('logoutConfirmation.body'),
      onConfirm: () => openid.logout(),
    });
  };

  const items: ItemType[] = [
    {
      key: '/projects',
      label: <NavLink to="/projects">{t('projects.title')}</NavLink>,
    },
    hasRole('ADMIN') && {
      key: '/users',
      label: <NavLink to="/users">{t('users.title')}</NavLink>,
    },
  ].filter((menuItem) => !!menuItem) as ItemType[];

  const itemsAvatar: ItemType[] = [
    {
      key: '/user-actions',
      icon: (
        <div className={classes.avatarMenuWrapper}>
          <Avatar size={40} reference={avatar} alt={name} />
          <Typography.Text className={classes.nameText}>{name}</Typography.Text>
          <CaretDownFilled className={classes.caretIcon} />
        </div>
      ),
      className: 'user-actions',
      children: [
        {
          key: '/me',
          label: <NavLink to="/me">{name}</NavLink>,
        },
        {
          key: '/change-password',
          label: <NavLink to="/change-password">{t('nav.changePassword')}</NavLink>,
        },
        {
          key: '/logout',
          onClick: showSignOutConfirmationDialog,
          label: t('nav.logout'),
        },
      ].filter((menuItem) => !!menuItem) as ItemType[],
    },
  ];

  const selectedKeys = useSelectedNavMenuKeys(items);

  return (
    <div className={classes.navMenuWrapper}>
      <Menu selectedKeys={selectedKeys} className="nav" mode="horizontal" disabledOverflow items={items} />
      <div className={classes.avatarMenu}>
        <Menu selectedKeys={[]} className="nav" mode="horizontal" disabledOverflow items={itemsAvatar} />
      </div>
    </div>
  );
}

export function NavPanel() {
  const navMenu = useNavMenu();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [isShowBurgerMenu, setIsShowBurgerMenu] = useState(false);

  return (
    <div className={classes.navPanel}>
      <div className={classes.logoWrapper}>
        <LogoIcon />
        <NavLink to="/" className={classes.brand}>
          {t('brandFullName')}
        </NavLink>
      </div>
      {screens.sm ? (
        navMenu
      ) : (
        <>
          <Button icon={<MenuOutlined />} type="text" onClick={() => setIsShowBurgerMenu(true)} />
          <BurgerMenu isActive={isShowBurgerMenu} setInactive={() => setIsShowBurgerMenu(false)} />
        </>
      )}
    </div>
  );
}
